import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'

import { PolarAngleAxis, RadialBar, RadialBarChart } from 'recharts'

import type { ChartConfig } from '@/components/ui/chart'
import { EmptyState } from '@/components/common/empty-state'
import { ChartContainer } from '@/components/ui/chart'
import { PeriodConfigRequiredBanner } from '@/features/organization/components'
import {
  useMyConvexOrganizationId,
  AuthGuard,
  useCurrentOrganizationId,
} from '@/features/auth'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/components/ui/multi-select'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute('/(dashboard)/analytics/statistics/')({
  component: RouteComponent,
  beforeLoad: () => ({
    title: 'Statistics',
    breadcrumbs: [{ title: 'Analytics' }, { title: `Statistics` }],
  }),
})

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * A single aggregated bucket returned by api.analytics.getAnalyticsStats.
 * One row per (role, level, responseType) combination.
 *
 * avgScore    — weighted average on a 0-100 scale (matches assessmentAverages.overallAvg)
 * sampleCount — completed response count feeding this bucket
 * employeeCount — unique employees ASSESSED at this (role, level) across ALL
 *                 response types; same value for every responseType row at the
 *                 same level, safe to use without double-counting.
 */
type StatRow = {
  role: string
  level: string
  responseType: string
  avgScore: number
  sampleCount: number
  employeeCount: number
  talentAvg: number | null
  craftAvg: number | null
  resultsAvg: number | null
  directionAvg: number | null
  cultureAvg: number | null
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatPeriod(startsAt: number, endsAt: number): string {
  const start = new Date(startsAt)
  const end = new Date(endsAt)
  const sm = start.toLocaleDateString('en-US', { month: 'short' })
  const em = end.toLocaleDateString('en-US', { month: 'short' })
  return `${sm}-${em} ${start.getFullYear()}`
}

/**
 * Weighted average of avgScores, weighted by sampleCount.
 * Avoids the "average of averages" fallacy when buckets have different sizes.
 */
function weightedAvg(rows: StatRow[]): number {
  const totalSamples = rows.reduce((s, r) => s + r.sampleCount, 0)
  if (totalSamples === 0) return 0
  return rows.reduce((s, r) => s + r.avgScore * r.sampleCount, 0) / totalSamples
}

function sortLevels(levels: string[]): string[] {
  return [...levels].sort((a, b) => {
    const an = parseInt(a.replace(/\D/g, ''), 10) || 0
    const bn = parseInt(b.replace(/\D/g, ''), 10) || 0
    return an - bn
  })
}

// ── Route component ──────────────────────────────────────────────────────────

function RouteComponent() {
  const orgId = useMyConvexOrganizationId()
  const legacyOrgId = useCurrentOrganizationId()
  const [splitByLevels, setSplitByLevels] = useState(true)
  const [selectedRoles, setSelectedRoles] = useState<Array<string>>([])
  const [selectedPeriods, setSelectedPeriods] = useState<Array<string>>([])

  const periods = useQuery(
    api.organizationConfig.listPeriods,
    orgId ? { organizationId: orgId as Id<'organizations'> } : 'skip',
  )

  // Only the first selected period is used (consistent with original page).
  // Multiple periods would require merging buckets client-side — future work.
  const periodId = selectedPeriods[0] as Id<'periods'> | undefined

  const stats = useQuery(
    api.analytics.getAnalyticsStats,
    orgId
      ? {
          organizationId: orgId as Id<'organizations'>,
          ...(periodId ? { periodId } : {}),
        }
      : 'skip',
  )

  const statsLoading = stats === undefined
  const periodsLoading = periods === undefined

  // Derive available roles from the returned stats (only roles with data appear)
  const availableRoles = useMemo(
    () => [...new Set((stats ?? []).map((s) => s.role))].sort(),
    [stats],
  )

  const displayRoles =
    selectedRoles.length > 0
      ? availableRoles.filter((r) => selectedRoles.includes(r))
      : availableRoles

  const handleClearFilters = () => {
    setSelectedRoles([])
    setSelectedPeriods([])
  }

  return (
    <AuthGuard
      requiredOrganizationRole={{
        organizationId: legacyOrgId || '',
        role: ['admin', 'analytics', 'manager'],
      }}
    >
      <div className="space-y-6">
        <PeriodConfigRequiredBanner />

        {/* Header Section */}
        <div className="flex items-center justify-between p-6 bg-muted/30 rounded-lg">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Statistics
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Assessment performance by role and level
            </p>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-col gap-4 p-5 bg-muted/30 rounded-lg lg:flex-row lg:items-center lg:justify-between">
          {/* Split by Levels */}
          <div className="flex items-center space-x-2.5">
            <Checkbox
              id="split-levels"
              checked={splitByLevels}
              onCheckedChange={(checked) =>
                setSplitByLevels(checked as boolean)
              }
              className="bg-background"
            />
            <Label
              htmlFor="split-levels"
              className="text-sm font-medium cursor-pointer"
            >
              Split by Levels
            </Label>
          </div>

          {/* Roles and Periods Filters */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
            {/* Roles Filter — populated from stats (roles with no data are omitted) */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <Label className="text-sm font-medium whitespace-nowrap">
                Roles:
              </Label>
              {statsLoading ? (
                <Skeleton className="h-9 w-full sm:w-56" />
              ) : (
                <MultiSelect
                  values={selectedRoles}
                  onValuesChange={setSelectedRoles}
                >
                  <MultiSelectTrigger className="w-full sm:w-56 bg-background">
                    <MultiSelectValue placeholder="Select roles..." />
                  </MultiSelectTrigger>
                  <MultiSelectContent>
                    <MultiSelectGroup>
                      {availableRoles.map((role) => (
                        <MultiSelectItem key={role} value={role}>
                          {role}
                        </MultiSelectItem>
                      ))}
                    </MultiSelectGroup>
                  </MultiSelectContent>
                </MultiSelect>
              )}
            </div>

            {/* Periods Filter */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <Label className="text-sm font-medium whitespace-nowrap">
                Periods:
              </Label>
              {periodsLoading ? (
                <Skeleton className="h-9 w-full sm:w-56" />
              ) : (
                <MultiSelect
                  values={selectedPeriods}
                  onValuesChange={setSelectedPeriods}
                >
                  <MultiSelectTrigger className="w-full sm:w-56 bg-background">
                    <MultiSelectValue placeholder="Select periods..." />
                  </MultiSelectTrigger>
                  <MultiSelectContent>
                    <MultiSelectGroup>
                      {(periods ?? []).map((p) => (
                        <MultiSelectItem key={p._id} value={p._id}>
                          {formatPeriod(p.startsAt, p.endsAt)}
                        </MultiSelectItem>
                      ))}
                    </MultiSelectGroup>
                  </MultiSelectContent>
                </MultiSelect>
              )}
            </div>
          </div>

          {/* Clear Button */}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClearFilters}
            disabled={
              selectedRoles.length === 0 && selectedPeriods.length === 0
            }
            className="h-9 w-full sm:w-auto"
          >
            Clear Filters
          </Button>
        </div>

        {/* Loading skeleton */}
        {statsLoading && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-64 w-full rounded-lg" />
            ))}
          </div>
        )}

        {/* Empty state — no data at all */}
        {!statsLoading && displayRoles.length === 0 && (
          <EmptyState
            title="No assessment data"
            description="Complete some assessments to see statistics here."
          />
        )}

        {/* Role Cards Grid */}
        {!statsLoading && displayRoles.length > 0 && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {displayRoles.map((role) => (
              <RoleCard
                key={role}
                role={role}
                stats={(stats ?? []).filter((s) => s.role === role)}
                splitByLevels={splitByLevels}
              />
            ))}
          </div>
        )}
      </div>
    </AuthGuard>
  )
}

// ── RoleCard ─────────────────────────────────────────────────────────────────

interface RoleCardProps {
  role: string
  /** Pre-aggregated stats for this role — all (level, responseType) combos */
  stats: StatRow[]
  splitByLevels: boolean
}

function RoleCard({ role, stats, splitByLevels }: RoleCardProps) {
  const [localResponseType, setLocalResponseType] = useState<
    string | undefined
  >()
  const [localLevel, setLocalLevel] = useState<string | undefined>()

  // Levels present in the data for this role, numerically sorted
  const levels = useMemo(
    () => sortLevels([...new Set(stats.map((s) => s.level))]),
    [stats],
  )

  // Apply local response-type filter; level filter is passed to the views
  const filteredStats = localResponseType
    ? stats.filter((s) => s.responseType === localResponseType)
    : stats

  const handleClearLocalFilters = () => {
    setLocalResponseType(undefined)
    setLocalLevel(undefined)
  }

  return (
    <div className="rounded-lg bg-muted/30 p-6">
      <div className="space-y-6">
        {/* Header with per-card filters */}
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-lg font-semibold tracking-tight">{role}</h3>
          <div className="flex gap-2 flex-wrap justify-end">
            {/* Response Type Filter */}
            <Select
              value={localResponseType}
              onValueChange={setLocalResponseType}
            >
              <SelectTrigger className="w-32 bg-background">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="self">Self</SelectItem>
              </SelectContent>
            </Select>

            {/* Level Filter */}
            <Select value={localLevel} onValueChange={setLocalLevel}>
              <SelectTrigger className="w-32 bg-background">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                {levels.map((level) => (
                  <SelectItem key={level} value={level}>
                    {level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(localResponseType ?? localLevel) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearLocalFilters}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        {filteredStats.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No assessment data for this filter
          </p>
        ) : splitByLevels ? (
          <LevelSplitView
            stats={filteredStats}
            levels={localLevel ? [localLevel] : levels}
          />
        ) : (
          <AggregateView stats={filteredStats} />
        )}
      </div>
    </div>
  )
}

// ── LevelSplitView ───────────────────────────────────────────────────────────

interface LevelSplitViewProps {
  stats: StatRow[]
  /** Ordered list of levels to render (may be filtered to a single level) */
  levels: string[]
}

function LevelSplitView({ stats, levels }: LevelSplitViewProps) {
  if (levels.length === 0) {
    return <EmptyState title="No positions found" />
  }

  return (
    <div className="space-y-4">
      {levels.map((level) => {
        const levelStats = stats.filter((s) => s.level === level)
        const hasData = levelStats.length > 0
        const avgScore = hasData ? weightedAvg(levelStats) : 0
        // employeeCount is deduplicated across response types in the Convex query;
        // all rows at the same (role, level) carry the same value.
        const employeeCount = levelStats[0]?.employeeCount ?? 0

        return (
          <div
            key={level}
            className="rounded-xl bg-background/50 p-4 border border-border/50"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold">{level}</h4>
              {hasData && (
                <span className="text-sm font-medium">
                  {avgScore.toFixed(1)}%
                </span>
              )}
            </div>
            {hasData ? (
              <>
                <Progress value={avgScore} className="h-2 mb-3" />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Employees in bracket</span>
                  <span className="font-medium">{employeeCount}</span>
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-2">
                No data
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── AggregateView ─────────────────────────────────────────────────────────────

interface AggregateViewProps {
  stats: StatRow[]
}

function AggregateView({ stats }: AggregateViewProps) {
  const avgScore = weightedAvg(stats)

  // Sum employee counts across distinct levels (employeeCount per row is already
  // deduplicated at the (role, level) level, so summing unique levels is correct).
  const seenLevels = new Set<string>()
  const employeeCount = stats.reduce((total, s) => {
    if (!seenLevels.has(s.level)) {
      seenLevels.add(s.level)
      return total + s.employeeCount
    }
    return total
  }, 0)

  if (stats.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No assessment data available
      </p>
    )
  }

  const chartData = [
    { name: 'Progress', score: avgScore, fill: 'var(--chart-1)' },
  ]

  const chartConfig = {
    score: { label: 'Score', color: 'var(--chart-1)' },
  } satisfies ChartConfig

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-center py-4">
        <ChartContainer
          config={chartConfig}
          className="mx-auto aspect-square h-[280px] w-[280px]"
        >
          <RadialBarChart
            data={chartData}
            startAngle={90}
            endAngle={-270}
            innerRadius="60%"
            outerRadius="100%"
          >
            <PolarAngleAxis
              type="number"
              domain={[0, 100]}
              angleAxisId={0}
              tick={false}
            />
            <RadialBar dataKey="score" background cornerRadius={10} />
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-foreground"
            >
              <tspan
                x="50%"
                fontSize="32"
                fontWeight="700"
                className="fill-foreground"
              >
                {avgScore.toFixed(1)}%
              </tspan>
            </text>
          </RadialBarChart>
        </ChartContainer>
      </div>
      <div className="rounded-xl bg-background/50 p-4 border border-border/50">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Total employees</span>
          <span className="font-medium">{employeeCount}</span>
        </div>
      </div>
    </div>
  )
}
